import { readsApi, writesApi } from './api';
import type {
  HeldSemantics,
  HoldBreakdownSums,
  HoldClass,
  LedgerWallet,
  WalletOwnerType,
  WalletStatementRow,
} from './ledger.service';

// ── Treasury: the "move the money" operator API ────────────────────────────
// Writes live in Spring Boot (:8080) under /admin/treasury. Every write is a
// double-entry journal posting — nothing here updates a wallet balance
// directly. Reads for the owner ledger live in FastAPI (:8000).
//
// The contract every treasury POST honours:
//   • `preview: true` performs a dry run — it computes the projected balances
//     and rolls back, so nothing is written. This is what the UI calls first.
//   • `idempotencyKey` makes a repeat of the SAME attempt a no-op replay
//     (flagged back as `idempotentReplay`) instead of a second money movement.

export type TreasuryOperation =
  | 'refund-customer'
  | 'refund-merchant'
  | 'credit'
  | 'debit'
  | 'transfer'
  | 'adjust-hold';

/** Owner types that can have a wallet provisioned. `system` wallets are seeded. */
export type ProvisionableOwnerType = 'user' | 'business' | 'branch';

// ── Operation results ──────────────────────────────────────────────────────

/** One wallet touched by the operation, at its post-operation state.
 *  On a preview these are *projected* — nothing has been written. */
export interface TreasuryBalance {
  walletId: string;
  walletType: string;
  ownerType: WalletOwnerType | string;
  balance: number;
  heldBalance: number;
  availableBalance: number;
}

export interface TreasuryResult {
  operation: string;
  /** Null on a preview — no entry is posted during a dry run. */
  journalEntryId: string | null;
  amount: number;
  reason: string;
  /** True when this response came from `preview: true`. */
  preview: boolean;
  /** True when the idempotency key had already been applied — no new money moved. */
  idempotentReplay: boolean;
  balances: TreasuryBalance[];
}

// ── Operation request ──────────────────────────────────────────────────────

export interface TreasuryRequest {
  walletId: string;
  amount: number;
  /** Required by the backend and by this UI — an unexplained money movement
   *  is unauditable. */
  reason: string;
  /** Transfer only: where the money goes. */
  counterpartyWalletId?: string;
  /** adjust-hold only. The backend validates `amount` as strictly positive
   *  (@DecimalMin 0.01) and takes the direction from THIS field — a signed
   *  amount would be rejected with a 400. Defaults to 'unhold' server-side. */
  direction?: 'hold' | 'unhold';
  referenceType?: string;
  referenceId?: string;
  idempotencyKey?: string;
  preview?: boolean;
}

const OPERATION_PATH: Record<TreasuryOperation, string> = {
  'refund-customer': '/admin/treasury/refund-customer',
  'refund-merchant': '/admin/treasury/refund-merchant',
  credit: '/admin/treasury/credit',
  debit: '/admin/treasury/debit',
  transfer: '/admin/treasury/transfer',
  'adjust-hold': '/admin/treasury/adjust-hold',
};

// ── Wallet types + provisioning ────────────────────────────────────────────

export interface TreasuryWalletType {
  code: string;
  name: string;
  ownerType: WalletOwnerType | string;
  isSystem: boolean;
}

export interface ProvisionWalletRequest {
  ownerType: ProvisionableOwnerType;
  ownerId: string;
  walletTypeCode: string;
}

/** Idempotent: `created` is false when the wallet already existed. */
export interface ProvisionWalletResult {
  created: boolean;
  walletId?: string;
  walletTypeCode?: string;
  walletTypeName?: string | null;
  ownerType?: WalletOwnerType | string;
  ownerId?: string;
  balance?: number;
  heldBalance?: number;
  availableBalance?: number;
  currency?: string;
  status?: string;
}

// ── Hold worklist ──────────────────────────────────────────────────────────

/** One outstanding hold, grouped by the reference it was placed against.
 *  A hold sitting on a terminal basket is money nobody can ever spend. */
export interface TreasuryHold {
  walletId: string;
  walletTypeCode?: string | null;
  referenceType: string | null;
  referenceId: string | null;
  amount: number;
  /** `order_baskets.status` for the referenced basket, when resolvable. */
  basketStatus?: string | null;
  description?: string | null;
  createdAt?: string | null;
}

// ── Owner ledger (FastAPI reads) ───────────────────────────────────────────

export interface OwnerLedgerOwner {
  ownerType: WalletOwnerType | string;
  ownerId: string;
  label: string | null;
}

/** A wallet type this owner *should* have but does not. Money addressed to a
 *  wallet that does not exist is the single most common "the payout failed"
 *  cause — provisioning is the fix. */
export interface MissingWalletType {
  code: string;
  name: string;
}

/** The reads service emits `sum*` names here — matching them exactly is what
 *  makes the owner-ledger stat cards render a number instead of `undefined`. */
export interface OwnerLedgerTotals extends HoldBreakdownSums {
  walletCount: number;
  sumBalance: number;
  sumHeld: number;
  /** Raw generated column (balance − held). Demoted to small print. */
  sumAvailable: number;
}

/** Same shape as a wallet statement row, plus which of the owner's wallets it
 *  belongs to (the owner view spans several wallets). */
export interface OwnerLedgerTransaction extends WalletStatementRow {
  walletId?: string | null;
  walletTypeCode?: string | null;
}

export interface OwnerOutstandingHold {
  referenceId: string | null;
  referenceType: string | null;
  basketStatus: string | null;
  /** `incoming` = basket still in flight (normal), `stranded` = basket already
   *  finished (the leak), `onBalance` = not a basket at all (treasury hold). */
  holdClass?: HoldClass | null;
  walletId: string;
  walletTypeCode: string | null;
  amount: number;
  createdAt?: string | null;
}

export interface OwnerWithdrawalRequest {
  id: string;
  walletId: string | null;
  walletTypeCode?: string | null;
  amount: number;
  status: string;
  description?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
  createdAt: string | null;
  completedAt?: string | null;
}

export interface OwnerLedger {
  owner: OwnerLedgerOwner;
  wallets: LedgerWallet[];
  missingWalletTypes: MissingWalletType[];
  totals: OwnerLedgerTotals;
  recentTransactions: OwnerLedgerTransaction[];
  outstandingHolds: OwnerOutstandingHold[];
  withdrawalRequests: OwnerWithdrawalRequest[];
  heldSemantics?: HeldSemantics;
}

// ── Basket statuses past which a hold is stuck money ────────────────────────
// Mirrors the reads service's `_TERMINAL_BASKET_STATUSES`.
export const TERMINAL_BASKET_STATUSES = [
  'confirmed',
  'partially_confirmed',
  'cancelled',
  'rejected_by_merchant',
  'refunded',
  'returned',
];

/** A hold is only stuck once the basket it is earmarked against has finished.
 *  Prefers the API's own `holdClass` when present — the status list is the
 *  fallback for older payloads. */
export function isStuckHold(
  basketStatus: string | null | undefined,
  holdClass?: HoldClass | null,
): boolean {
  if (holdClass) return holdClass === 'stranded';
  return !!basketStatus && TERMINAL_BASKET_STATUSES.includes(basketStatus);
}

// ── Stranded holds: the provenance worklist ────────────────────────────────
// `GET /admin/reconciliation/stranded-holds` (FastAPI). One item per stuck
// earmark, carrying the whole story: how the money entered, what has happened
// to it since, and where it is SUPPOSED to end up. This is what the clear-hold
// screen renders — the operator should never have to work an amount out.

/** What the system says should happen to this hold. */
export type StrandedVerdict =
  | 'refund_customer'
  | 'pay_out_split'
  | 'clear_hold_only';

/** A wallet the money is owed to, when the verdict is `pay_out_split`.
 *  `amount: null` is the honest answer when the split cannot be resolved — the
 *  reads service never substitutes 0. `note` carries the blocker, most often
 *  "no wallet exists yet — it must be provisioned before payout". */
export interface StrandedDestination {
  party: string;
  walletId?: string | null;
  walletLabel?: string | null;
  amount?: number | null;
  basis?: string | null;
  note?: string | null;
}

export interface StrandedIntent {
  verdict: StrandedVerdict | string;
  /** Prose, already written for a human — render it verbatim. */
  reason: string;
  /** Empty for `clear_hold_only`: crediting anyone would fabricate money. */
  destinations: StrandedDestination[];
  recommendedAction: string;
}

/** Whether the money behind this hold already moved, and where. */
export interface StrandedCurrentState {
  released: boolean;
  releasedAt?: string | null;
  releaseTransactionCount?: number;
  escrowReversed: boolean;
  refundedToCustomer?: number | null;
  refundedAt?: string | null;
  reversalEntries?: { entryId: string; description: string; postedAt: string }[];
  /** Cash still sitting in escrow for this basket. 0 ⇒ nothing left to move. */
  escrowStillHolding?: number | null;
  escrowStillHoldingBasis?: string | null;
}

export interface StrandedPaymentTransaction {
  id?: string | null;
  amount?: number | null;
  netAmount?: number | null;
  status?: string | null;
  direction?: string | null;
  method?: string | null;
  /** The M-Pesa receipt the customer can quote — NOT `externalReference`. */
  externalTransactionId?: string | null;
  mpesaCheckoutRequestId?: string | null;
  failureReason?: string | null;
  completedAt?: string | null;
  createdAt?: string | null;
}

export interface StrandedOrigin {
  paymentTransactions?: StrandedPaymentTransaction[];
  escrowCredited?: number | null;
  escrowCreditedBasis?: string | null;
  firstSeenAt?: string | null;
}

export interface StrandedHold {
  walletId: string;
  walletLabel: string;
  walletTypeCode: string;
  /** THE ceiling. The backend refuses anything above this for this basket. */
  amount: number;
  firstHeldAt: string | null;

  basketId: string;
  basketStatus: string;
  orderId: string | null;
  orderStatus: string | null;
  paymentStatus: string | null;
  businessName?: string | null;
  customerName?: string | null;
  customerId?: string | null;

  totalAmount?: number | null;
  orderTotalAmount?: number | null;
  cancelledAt?: string | null;
  confirmedAt?: string | null;

  origin: StrandedOrigin;
  currentState: StrandedCurrentState;
  intended: StrandedIntent;
  transactions?: OwnerLedgerTransaction[];
}

export interface StrandedHoldsResponse {
  items: StrandedHold[];
  count: number;
  /** verdict key → the general rule, for a legend. */
  verdicts?: Record<string, string>;
  generatedAt: string;
}

/** Verdict → whether clearing the hold is the WHOLE job. `clear_hold_only`
 *  means the cash already reached its destination, so the earmark is the only
 *  thing left to remove. The other two need money moved FIRST — clearing the
 *  hold alone would bury the fact that someone is still owed. */
export function clearingIsSufficient(verdict: string): boolean {
  return verdict === 'clear_hold_only';
}

// ── Idempotency ────────────────────────────────────────────────────────────

/** One key per apply attempt: a double-click replays instead of double-applying.
 *  `crypto.randomUUID` needs a secure context, so fall back for plain http dev. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// ── Service ────────────────────────────────────────────────────────────────

export const treasuryService = {
  /** Run any treasury operation. Pass `preview: true` on `payload` for a dry run. */
  execute: (operation: TreasuryOperation, payload: TreasuryRequest) =>
    writesApi.post<TreasuryResult>(OPERATION_PATH[operation], payload),

  /** Dry run: returns projected balances, writes nothing. */
  preview: (operation: TreasuryOperation, payload: TreasuryRequest) =>
    writesApi.post<TreasuryResult>(OPERATION_PATH[operation], { ...payload, preview: true }),

  /** The real thing. Always call `preview` first and get an explicit confirm. */
  apply: (operation: TreasuryOperation, payload: TreasuryRequest) =>
    writesApi.post<TreasuryResult>(OPERATION_PATH[operation], { ...payload, preview: false }),

  /** Seeded wallet types — drives the provisioning dropdown. */
  listWalletTypes: () => writesApi.get<TreasuryWalletType[]>('/admin/treasury/wallet-types'),

  /** Idempotent: returns the existing wallet with `created: false` if present. */
  provisionWallet: (payload: ProvisionWalletRequest) =>
    writesApi.post<ProvisionWalletResult>('/admin/treasury/wallets/provision', payload),

  /** Per-reference hold worklist for one wallet. */
  getWalletHolds: (walletId: string) =>
    writesApi.get<TreasuryHold[]>(`/admin/treasury/wallets/${walletId}/holds`),

  /** Everything about one owner's money in a single call → FastAPI reads. */
  getOwnerLedger: (ownerType: string, ownerId: string) =>
    readsApi.get<OwnerLedger>(`/admin/owners/${ownerType}/${ownerId}/ledger`),

  /** Stranded holds with full provenance + the system's verdict. Scope to one
   *  wallet (the clear-hold screen) or one basket, or omit for the global
   *  worklist. */
  getStrandedHolds: (params?: { walletId?: string; basketId?: string; limit?: number }) =>
    // Second arg IS the query object — `makeApi` does `client.get(url, { params })`
    // itself. Wrapping it again silently sends `?params=[object Object]`.
    readsApi.get<StrandedHoldsResponse>(
      '/admin/reconciliation/stranded-holds',
      params as Record<string, unknown>,
    ),
};
